import { ModsPanelCompProps } from "./ModsPanel";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Space,
  Input,
  Row,
  Col,
  Select,
  Checkbox,
  Switch,
  InputNumber,
  DatePicker,
  Typography,
  Segmented,
} from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  COMMON_PATHS,
  IDynamicChangeMod,
  findLayersOrLayerAssets,
  getPath,
} from "@blings/blings-player";
import { modTypes } from "@blings/blings-player";
import { observer } from "mobx-react-lite";
import { JSONTreeSelect } from "../../BaseComps/JSONTreeSelect";
import { rootStore, useMst } from "../../stores/main";
import { JsEditor } from "../../components/Editor/JsEditor";
import { HoverTooltip, InfoTooltip } from "../../BaseComps/InfoTooltip";
import { theme } from "../../theme";
import { ColorPicker } from "../../BaseComps/ColorPicker";
import "./DynamicValueMod.scss";
import { ExperimentOptimizationTechnique } from "../../API";
import { DividerWithText } from "../../BaseComps/HorizontalDividerName";
import UploadWithTextarea from "../../components/Upload";
import {
  AiIcon,
  DataKeyIcon,
  ExperimentPlusIcon,
  LiveControlIcon,
  PaperIcon,
  ValueIcon,
} from "../../icons-and-animations/Icons";
import { sharedInputAllowClear } from "../antd-extensions/selectClearIcon";

const { Option } = Select;
export enum DynamicValueModTypes {
  input = "input",
  textarea = "textarea",
  number = "number",
  color = "color",
  boolean = "boolean",
  url = "url",
  media = "media",
  hex = "hex",
  cta = "cta",
  date = "date",
  percentage = "percentage",
}

const MEDIA_PLACEHOLDERS = {
  image: "https://picsum.photos/200/300",
  audio:
    "https://upload.wikimedia.org/wikipedia/commons/2/2c/Stay_Down_Here_Where_You_Belong_%281915%2C_Burr%29.mp3",
  video:
    "https://upload.wikimedia.org/wikipedia/commons/4/43/Mute_swan_and_cygnets_in_Prospect_Park_%2880243%29.webm",
};
type MediaKind = keyof typeof MEDIA_PLACEHOLDERS;

const MEDIA_EXTENSION_META: Record<
  string,
  { kind: MediaKind; mime: string }
> = {
  ".mp4": { kind: "video", mime: "video/mp4" },
  ".mov": { kind: "video", mime: "video/quicktime" },
  ".m4v": { kind: "video", mime: "video/mp4" },
  ".webm": { kind: "video", mime: "video/webm" },
  ".avi": { kind: "video", mime: "video/x-msvideo" },
  ".mkv": { kind: "video", mime: "video/x-matroska" },
  ".flv": { kind: "video", mime: "video/x-flv" },
  ".mp3": { kind: "audio", mime: "audio/mpeg" },
  ".aac": { kind: "audio", mime: "audio/aac" },
  ".wav": { kind: "audio", mime: "audio/wav" },
  ".ogg": { kind: "audio", mime: "audio/ogg" },
  ".m4a": { kind: "audio", mime: "audio/mp4" },
  ".jpg": { kind: "image", mime: "image/jpeg" },
  ".jpeg": { kind: "image", mime: "image/jpeg" },
  ".png": { kind: "image", mime: "image/png" },
  ".webp": { kind: "image", mime: "image/webp" },
};

const DEFAULT_MEDIA_META: { kind: MediaKind; mime: string } = {
  kind: "image",
  mime: "",
};

const DEFAULT_TEXT_VALUE = "dynamic text";
const DEFAULT_COLOR_VALUE = "#ff00ff";
const DEFAULT_LINK_VALUE = "example.com";

const isStructuredValue = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

type JSONSchema7 = Record<string, any>;

type SchemaFieldResult = {
  schema: JSONSchema7;
  value: any;
};

const creatSchemaAndValue = (
  type: "string" | "number" | "boolean",
  value: any,
  format?: string
): SchemaFieldResult => {
  return {
    schema: {
      type,
      ...(format ? { format } : {}),
      default: value,
      examples: [value],
    },
    value,
  };
};

const getMediaMetadataFromAsset = (
  assetName?: string
): { kind: MediaKind; mime: string } => {
  if (!assetName) return DEFAULT_MEDIA_META;
  const lower = assetName.toLowerCase();
  const matchedExt = Object.keys(MEDIA_EXTENSION_META).find((ext) =>
    lower.endsWith(ext)
  );
  if (matchedExt) {
    return MEDIA_EXTENSION_META[matchedExt];
  }
  return DEFAULT_MEDIA_META;
};

const getMimeTypeFromAssetName = (assetName?: string) =>
  getMediaMetadataFromAsset(assetName).mime;

const getSchemaFieldConfigForMod = (
  modType: modTypes | undefined,
  assetName?: string
): SchemaFieldResult => {
  switch (modType) {
    case modTypes.text:
      return creatSchemaAndValue("string", DEFAULT_TEXT_VALUE, "text");
    case modTypes.themeColor:
    case modTypes.color:
      return creatSchemaAndValue("string", DEFAULT_COLOR_VALUE, "color");
    case modTypes.interactiveOpen:
      return creatSchemaAndValue("string", DEFAULT_LINK_VALUE, "link");
    case modTypes.asset: {
      const { kind: mediaKind } = getMediaMetadataFromAsset(assetName);
      const placeholder = MEDIA_PLACEHOLDERS[mediaKind];
      return creatSchemaAndValue("string", placeholder, mediaKind);
    }
    case modTypes.hideLayer:
      return creatSchemaAndValue("boolean", true);
    case modTypes.interactiveGoto:
      return creatSchemaAndValue("number", 30);
    case modTypes.custom:
    case modTypes.transform:
      return creatSchemaAndValue("number", 0);
    default:
      return creatSchemaAndValue("string", DEFAULT_TEXT_VALUE, "text");
  }
};

dayjs.extend(utc);
dayjs.extend(timezone);

export type dynamicValueType =
  | "value"
  | "dataKey"
  | "expression"
  | "liveControlKey"
  | "inputName"
  | "ctaName"
  | "exposeToPlatform"
  | "experimentId";

type CustomValueEditorProps = {
  value: any;
  onChange: (value: any) => void;
};

interface Props<T extends modTypes> {
  index: ModsPanelCompProps<any>["index"];
  modId?: ModsPanelCompProps<any>["modId"];
  onChange: ModsPanelCompProps<IDynamicChangeMod<T, any>>["onChange"];
  type?: DynamicValueModTypes;
  change: ModsPanelCompProps<IDynamicChangeMod<T, any>>["change"];
  uploaderProps?: { accept: string; s3Folder: string };
  booleanText?: [string, string];
  jsonVid?: any;
  assetName?: string;
  onChangeType?: (type: dynamicValueType) => void;
  modT?: T;
  dividerText?: string;
  dataFieldConfig?: SchemaFieldResult;
  expressionPlaceholder?: string;
  expressionTooltipText?: string;
  sourceDefaults?: Partial<Record<dynamicValueType, unknown>>;
  sourceHelpText?: ReactNode;
  renderValueEditor?: (props: CustomValueEditorProps) => ReactNode;
  renderDefaultValueEditor?: (props: CustomValueEditorProps) => ReactNode;
  // onChange: (index: number, key: keyof T, value: any) => void;
}

type DynamicValueSourceType = Extract<
  dynamicValueType,
  "value" | "dataKey" | "expression" | "liveControlKey" | "inputName"
>;

type DynamicValueDraftCache = Partial<Record<DynamicValueSourceType, any>>;

const dynamicValueSourceTypes: DynamicValueSourceType[] = [
  "value",
  "dataKey",
  "expression",
  "liveControlKey",
  "inputName",
];
const dynamicValueDraftCacheByConnector = new Map<
  string,
  DynamicValueDraftCache
>();

const isDynamicValueSourceType = (
  type: dynamicValueType
): type is DynamicValueSourceType =>
  dynamicValueSourceTypes.includes(type as DynamicValueSourceType);

const getDraftCache = (key: string): DynamicValueDraftCache => {
  let cache = dynamicValueDraftCacheByConnector.get(key);
  if (!cache) {
    cache = {};
    dynamicValueDraftCacheByConnector.set(key, cache);
  }
  return cache;
};

const readDraftValue = (
  cache: DynamicValueDraftCache,
  type: DynamicValueSourceType,
  fallback: any
): any =>
  Object.prototype.hasOwnProperty.call(cache, type) ? cache[type] : fallback;

// defaultValue
function DynamicValueModComp<T extends modTypes>({
  index,
  modId = index,
  onChange,
  change,
  type = DynamicValueModTypes.input,
  uploaderProps,
  booleanText,
  jsonVid,
  assetName,
  onChangeType,
  modT,
  dividerText,
  dataFieldConfig,
  expressionPlaceholder: expressionPlaceholderOverride,
  expressionTooltipText,
  sourceDefaults,
  sourceHelpText,
  renderValueEditor,
  renderDefaultValueEditor,
}: Props<T>) {
  const isMediaMod = change.type.startsWith("asset");
  const isColorMod = change.type.startsWith("themeColor");
  const isTextMod = change.type.startsWith("text");
  const isCountdownMod = change.type.startsWith("countdown");
  const isTransform = change.type.startsWith("transform");
  const canExposeToPlatform = true; //rootStore.platformStore.isControlDeprecated;
  const hasExperimentsEnabled =
    rootStore.experimentStore.selectedExperimentOptimizationTechnique &&
    rootStore.experimentStore.selectedExperimentOptimizationTechnique !==
      ExperimentOptimizationTechnique.DISABLED;

  const experimentId = change.experimentId;
  const experimentData =
    experimentId !== undefined
      ? rootStore.experimentStore.getExperiment(experimentId)
      : undefined;
  const experimentVariants =
    experimentId !== undefined ? experimentData?.variants : undefined;
  const hasExperiment =
    experimentId !== undefined &&
    experimentVariants &&
    experimentVariants.length >= 2;
  const [isExposedToPlatform, setIsExposedToPlatform] = useState<
    boolean | undefined
  >(change.exposeToPlatform);

  const draftCacheKey = [
    rootStore.platformStore.selectedProjectId || "local-project",
    rootStore.platformStore.selectedVideoPartName || "local-scene",
    modId,
    change.type,
  ].join(":");
  const draftCache = getDraftCache(draftCacheKey);

  const hasValue = typeof change.value !== "undefined";
  const hasDataKey = typeof change.dataKey !== "undefined";
  const hasLiveControlKey = typeof change.liveControlKey !== "undefined";
  const hasExpression = typeof change.expression !== "undefined";
  const hasInputName = typeof change.inputName !== "undefined";
  const hasStructuredValue = isStructuredValue(change.value);
  const valueContainsTemplateString =
    typeof change.value === "string" && /\$\{.*?\}/.test(change.value);

  const pickValueType = useCallback(
    (currentType: dynamicValueType): dynamicValueType => {
      if (hasValue) return "value";
      if (hasDataKey) return "dataKey";
      if (hasLiveControlKey) return "liveControlKey";
      if (hasExpression) return "expression";
      if (hasInputName) return "inputName";
      return currentType;
    },
    [hasValue, hasDataKey, hasLiveControlKey, hasExpression, hasInputName]
  );

  const [valueSetterType, setValueType] = useState<dynamicValueType>(() =>
    pickValueType(isCountdownMod || isTransform ? "value" : "dataKey")
  );
  const lastNotifiedTypeRef = useRef<dynamicValueType>(valueSetterType);
  const [update, setUpdate] = useState<number>(0);

  const getChangeValueForType = useCallback(
    (type: DynamicValueSourceType) => {
      switch (type) {
        case "value":
          return change.value;
        case "dataKey":
          return change.dataKey;
        case "liveControlKey":
          return change.liveControlKey;
        case "inputName":
          return change.inputName;
        case "expression":
          return change.expression;
      }
    },
    [
      change.value,
      change.dataKey,
      change.liveControlKey,
      change.inputName,
      change.expression,
    ]
  );

  const rememberValueForType = useCallback(
    (type: DynamicValueSourceType) => {
      draftCache[type] = getChangeValueForType(type);
    },
    [draftCache, getChangeValueForType]
  );

  useEffect(() => {
    if (!isDynamicValueSourceType(valueSetterType)) return;
    rememberValueForType(valueSetterType);
  }, [rememberValueForType, valueSetterType]);

  const handleValueTypeChange = (changeType: dynamicValueType) => {
    if (isDynamicValueSourceType(valueSetterType)) {
      rememberValueForType(valueSetterType);
    }

    setValueType(changeType);
    onChangeType?.(changeType);
    lastNotifiedTypeRef.current = changeType;
    if (!isDynamicValueSourceType(changeType)) return;

    const draftValue = readDraftValue(
      draftCache,
      changeType,
      getChangeValueForType(changeType)
    );
    const nextValue =
      typeof draftValue === "undefined"
        ? sourceDefaults?.[changeType]
        : draftValue;

    changeValWithValueSetterType(nextValue, changeType);
  };

  const changeValWithValueSetterType = useCallback(
    (value, updatedValueSetterType) => {
      const types: Array<dynamicValueType> = [
        "value",
        "dataKey",
        "expression",
        "liveControlKey",
        "inputName",
      ];
      if (isDynamicValueSourceType(updatedValueSetterType)) {
        draftCache[updatedValueSetterType] = value;
      }
      onChange(
        index,
        types,
        types.map((t) => (t === updatedValueSetterType ? value : undefined))
      );
    },
    [draftCache, index, onChange]
  );

  const changeModExperimentId = useCallback(
    (experimentId: string) => {
      onChange(index, ["experimentId"], [experimentId]);
    },
    [index, onChange]
  );

  const changeVal = useCallback(
    (value) => {
      changeValWithValueSetterType(value, valueSetterType);
    },
    [changeValWithValueSetterType, valueSetterType]
  );

  const changeExposeToPlatformVal = useCallback(
    (value) => {
      onChange(index, ["exposeToPlatform"], [value]);
    },
    [index, onChange]
  );

  const changeDefaultVal = useCallback(
    (defaultValue) => {
      onChange(index, ["defaultValue"], [defaultValue]);
    },
    [index, onChange]
  );

  type ChangeLayerBindings = Partial<{
    assetId: string;
    layerName: string;
    layerUid: number;
    additionals: Array<{
      assetId?: string;
      layerName?: string;
      layerUid?: number;
    }>;
  }>;

  const {
    value: changeValue,
    assetId: changeAssetId,
    layerName: changeLayerName,
    layerUid: changeLayerUid,
    additionals: changeAdditionals,
    exposeToPlatform,
  } = change as IDynamicChangeMod<T, any> & ChangeLayerBindings;

  useEffect(() => {
    const detectedType = pickValueType(valueSetterType);
    if (detectedType !== valueSetterType) {
      setValueType(detectedType);
    }
    if (lastNotifiedTypeRef.current !== detectedType) {
      onChangeType?.(detectedType);
      lastNotifiedTypeRef.current = detectedType;
    }
  }, [
    hasValue,
    hasDataKey,
    hasLiveControlKey,
    hasExpression,
    hasInputName,
    isCountdownMod,
    isTransform,
    pickValueType,
    onChangeType,
    valueSetterType,
  ]);

  useEffect(() => {
    if (
      valueSetterType === "value" &&
      typeof changeValue === "undefined" &&
      type === DynamicValueModTypes.textarea
    ) {
      const layers = findLayersOrLayerAssets({
        assetId: changeAssetId,
        layerName: changeLayerName || "",
        layerUid: changeLayerUid,
        jsonVid,
        additionals: changeAdditionals,
      });
      if (layers?.length) {
        const layer = layers[0];
        const text: string = getPath(layer, COMMON_PATHS.TEXT_IN_LAYER); // If the layer has text
        changeVal(text);
      }
    }
    if (valueSetterType !== "value" && !isTextMod && exposeToPlatform) {
      changeExposeToPlatformVal(false);
      setIsExposedToPlatform(false);
    }
  }, [
    changeAssetId,
    changeLayerName,
    changeLayerUid,
    changeAdditionals,
    changeValue,
    exposeToPlatform,
    jsonVid,
    type,
    valueSetterType,
    changeVal,
    changeExposeToPlatformVal,
    isTextMod,
  ]);

  const {
    platformStore: { inputNames, uploadAssetToProject },
    dynamicDataStore: {
      perVideoCurrentSchema,
      liveControlCurrentSchema,
      addPerVideoDataField,
    },
    layoutStore: { setSchemaDrawerOpen, setActiveSchemaTab },
  } = useMst();

  const inputNameOption = Array.from(new Set(inputNames as string[])).map(
    (inputName) => <Option key={inputName}>{inputName}</Option>
  );

  const segmentedOptions = useMemo(() => {
    const CodeIcon = () => (
      <svg
        width="16"
        height="16"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8 9L11 6L8 3"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M4 3L1 6L4 9"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );

    const options: Array<{
      value: dynamicValueType;
      label: React.ReactNode;
    }> = [
      {
        value: "dataKey",
        label: (
          <HoverTooltip
            title="Data"
            description='Connect to data that can be dynamic for each video (e.g. "user_name").'
            placement="top"
          >
            <span className="segmented-option">
              <span className="segmented-icon">
                <DataKeyIcon />
              </span>
              <span className="segmented-text">Data</span>
            </span>
          </HoverTooltip>
        ),
      },
    ];

    if (!rootStore.platformStore.isControlDeprecated) {
      options.push({
        value: "liveControlKey",
        label: (
          <HoverTooltip
            title="Control"
            description="Connect to data that can be controlled from Blings platform in real time (e.g. marketing copy, discount price)."
            placement="top"
          >
            <span className="segmented-option">
              <span className="segmented-icon">
                <LiveControlIcon />
              </span>
              <span className="segmented-text">Control</span>
            </span>
          </HoverTooltip>
        ),
      });
    }

    options.push({
      value: "value",
      label: (
        <HoverTooltip
          title="Edit"
          description="Replace the video content with any custom value."
          placement="top"
        >
          <span className="segmented-option">
            <span className="segmented-icon">
              <ValueIcon />
            </span>
            <span className="segmented-text">Edit</span>
          </span>
        </HoverTooltip>
      ),
    });

    if (inputNames?.length && !isCountdownMod) {
      options.push({
        value: "inputName",
        label: (
          <HoverTooltip
            title="Input"
            description="Connect to input that can be dynamic from another scene."
            placement="top"
          >
            <span className="segmented-option">
              <span className="segmented-icon">
                <PaperIcon />
              </span>
              <span className="segmented-text">Input</span>
            </span>
          </HoverTooltip>
        ),
      });
    }

    options.push({
      value: "expression",
      label: (
        <HoverTooltip
          title="Code"
          description="Write Javascript code that returns a value. You can use the data object."
          placement="top"
        >
          <span className="segmented-option">
            <span className="segmented-icon">
              <CodeIcon />
            </span>
            <span className="segmented-text">Code</span>
          </span>
        </HoverTooltip>
      ),
    });

    return options;
  }, [inputNames, isCountdownMod]);
  const handleAddNewDataKey = useCallback(
    (key: string) => {
      try {
        const { schema, value: defaultValue } =
          dataFieldConfig || getSchemaFieldConfigForMod(change.type, assetName);
        addPerVideoDataField(key, schema, defaultValue);
        setSchemaDrawerOpen(true);
        setActiveSchemaTab("pervideo");
        draftCache.dataKey = key;
        changeVal(key);
        return true;
      } catch (error) {
        console.error("Failed to add new per-video data key", error);
        return false;
      }
    },
    [
      change.type,
      assetName,
      dataFieldConfig,
      addPerVideoDataField,
      setSchemaDrawerOpen,
      setActiveSchemaTab,
      draftCache,
      changeVal,
    ]
  );
  const showInput =
    type === DynamicValueModTypes.input ||
    type === DynamicValueModTypes.url ||
    type === DynamicValueModTypes.media ||
    type === DynamicValueModTypes.hex ||
    type === DynamicValueModTypes.cta;

  const [inputPlaceholder, defaultExpressionPlaceholder] = (() => {
    switch (type) {
      case DynamicValueModTypes.number:
      case DynamicValueModTypes.percentage:
        return ["10", `return data.number`];
      case DynamicValueModTypes.hex:
        return [
          "#ffaa01",
          `return data.userLikesPink ? "${theme.primary}" : "#fff"`,
        ];
      case DynamicValueModTypes.url:
      case DynamicValueModTypes.media:
        return ["URL", "return 'https://cats.com/assets/' + data.cat"];
      case DynamicValueModTypes.boolean:
        return ["true or false", "return !data.value"];
      case DynamicValueModTypes.input:
      default:
        // eslint-disable-next-line no-template-curly-in-string
        return ["Hi ${name}", "return 'Hi ' + data.name"];
    }
  })();
  const expressionPlaceholder =
    expressionPlaceholderOverride || defaultExpressionPlaceholder;
  function getDividerText(type: DynamicValueModTypes, modT?: T): string | null {
    if (modT) {
      switch (modT) {
        case modTypes.interactiveGoto:
          return "Go To frame:";
        case modTypes.custom:
          return "Replace with:";
      }
    }

    switch (type) {
      case DynamicValueModTypes.textarea:
      case DynamicValueModTypes.hex:
      case DynamicValueModTypes.number:
        return "Replace with:";
      case DynamicValueModTypes.boolean:
        return "Set Boolean value by:";
      case DynamicValueModTypes.url:
        return "Set URL Value:";
      case DynamicValueModTypes.media:
        return "Media Asset Source (URL):";
      case DynamicValueModTypes.cta:
        return "CTA Value";
      case DynamicValueModTypes.date:
        return null;
      case DynamicValueModTypes.color:
        return "Color Value (HEX):";
      default:
        return "Source:";
    }
  }
  return (
    <Space direction="vertical" className={"DynamicValueModComp"}>
      <div>
        <DividerWithText text={dividerText || getDividerText(type, modT)!} />
      </div>
      <Segmented
        className="dynamic-value-tab-selector"
        value={valueSetterType}
        onChange={(value) => handleValueTypeChange(value as dynamicValueType)}
        options={segmentedOptions}
      />
      {valueSetterType === "value" && !renderValueEditor && (
        <div className="expose-to-platform">
          {canExposeToPlatform &&
            (isMediaMod || isColorMod || isTextMod || isCountdownMod) && (
              <div className="expose-to-platform-checkbox">
                <Checkbox
                  disabled={isCountdownMod}
                  checked={isExposedToPlatform || isCountdownMod}
                  onChange={(e) => {
                    setIsExposedToPlatform(e.target.checked);
                    changeExposeToPlatformVal(e.target.checked);
                  }}
                />
                <span>Enable editing in Blings Platform</span>
                <InfoTooltip
                  info={
                    isTextMod
                      ? "All text values are editable via the platform under 'Edit Video' -> 'Text'."
                      : isCountdownMod
                      ? "All Countdowns are editable via the platform under 'Media & Controls'."
                      : "Enable this option for easy value editing via Blings Platform under 'Edit Video' -> 'Media & Controls'."
                  }
                />
              </div>
            )}
        </div>
      )}
      {valueSetterType === "value" && (
        <Row style={{ display: "flex", alignItems: "center" }}>
          {renderValueEditor ? (
            renderValueEditor({ value: change.value, onChange: changeVal })
          ) : hasExperimentsEnabled && hasExperiment ? (
            <div className="experiment-viewport">
              <div className="experiment-container">
                {experimentVariants.map((variant, index) => {
                  return (
                    <div key={index}>
                      <DividerWithText
                        text={`Variant ${index + 1}`}
                        {...(index >= 1 && {
                          onIconClick: () => {
                            rootStore.experimentStore.deleteVariantByIndex(
                              experimentId,
                              index
                            );
                            if (experimentVariants.length <= 1) {
                              changeModExperimentId("");
                              changeVal(experimentVariants[0].value);
                            }
                            setUpdate(update + 1);
                            rootStore.playerStore.runPlayer();
                          },
                        })}
                      />
                      {type === DynamicValueModTypes.hex && (
                        <div
                          id="color-picker-button"
                          style={{
                            marginRight: "0.5rem",
                            alignSelf: "center",
                            display: "flex",
                            flexDirection: "row",
                          }}
                        >
                          <Input
                            id="connector-input"
                            value={experimentVariants[index].value}
                            // eslint-disable-next-line no-template-curly-in-string
                            placeholder={inputPlaceholder}
                            onChange={(e) => {
                              setUpdate(update + 1);
                              const value = /^[1-9]\d*$/.test(e.target.value)
                                ? parseInt(e.target.value, 10)
                                : e.target.value; // If only digits without trailing 0, convert to number
                              rootStore.experimentStore.updateVariantByIndex(
                                experimentId,
                                index,
                                value.toString()
                              );
                              rootStore.playerStore.runPlayer();
                              // changeVal(value);
                            }}
                            allowClear={sharedInputAllowClear}
                          />
                          <ColorPicker
                            color={experimentVariants[index].value || ""}
                            onChange={(e) => {
                              setUpdate(update + 1);
                              rootStore.experimentStore.updateVariantByIndex(
                                experimentId,
                                index,
                                e.hex
                              );
                              rootStore.playerStore.runPlayer();
                              // changeVal(e.hex);
                            }}
                            size={2}
                          />
                        </div>
                      )}

                      {type === DynamicValueModTypes.media && (
                        <UploadWithTextarea
                          uploadAssetsToProject={uploadAssetToProject}
                          onChange={(e) => {
                            setUpdate(update + 1);
                            const value = /^[1-9]\d*$/.test(e)
                              ? parseInt(e, 10)
                              : e; // If only digits without trailing 0, convert to number
                            rootStore.experimentStore.updateVariantByIndex(
                              experimentId,
                              index,
                              value.toString()
                            );
                            rootStore.playerStore.runPlayer();
                          }}
                          value={experimentVariants[index].value || ""}
                          mimeType={getMimeTypeFromAssetName(assetName)}
                          placeholder={inputPlaceholder}
                        />
                      )}
                      {(type === DynamicValueModTypes.input ||
                        type === DynamicValueModTypes.url ||
                        type === DynamicValueModTypes.cta) && (
                        <Input
                          id="connector-input"
                          value={experimentVariants[index].value}
                          // eslint-disable-next-line no-template-curly-in-string
                          placeholder={inputPlaceholder}
                          onChange={(e) => {
                            setUpdate(update + 1);
                            const value = /^[1-9]\d*$/.test(e.target.value)
                              ? parseInt(e.target.value, 10)
                              : e.target.value; // If only digits without trailing 0, convert to number
                            rootStore.experimentStore.updateVariantByIndex(
                              experimentId,
                              index,
                              value.toString()
                            );
                            rootStore.playerStore.runPlayer();
                            // changeVal(value);
                          }}
                          allowClear={sharedInputAllowClear}
                        />
                      )}
                      {(type === DynamicValueModTypes.number ||
                        type === DynamicValueModTypes.percentage) && (
                        <InputNumber
                          value={change.value}
                          onChange={(value) => {
                            setUpdate(update + 1);
                            rootStore.experimentStore.updateVariantByIndex(
                              experimentId,
                              index,
                              value
                            );
                            rootStore.playerStore.runPlayer();
                          }}
                          {...(type === DynamicValueModTypes.percentage
                            ? {
                                formatter: (value, info) => (value || 0) + "%",
                              }
                            : {})}
                          {...(type === DynamicValueModTypes.percentage
                            ? {
                                parser: (value) =>
                                  Number.parseFloat(
                                    value?.replace("%", "") || "0"
                                  ),
                              }
                            : {})}
                        />
                      )}
                      {type === DynamicValueModTypes.boolean && (
                        <>
                          <Switch checked={change.value} onChange={changeVal} />{" "}
                          {booleanText &&
                            (change.value ? booleanText[0] : booleanText[1])}
                        </>
                      )}

                      {type === DynamicValueModTypes.textarea && (
                        <Input.TextArea
                          id="connector-input"
                          placeholder={inputPlaceholder}
                          value={experimentVariants[index].value}
                          onChange={(e) => {
                            rootStore.experimentStore.updateVariantByIndex(
                              experimentId,
                              index,
                              e.target.value
                            );
                            setUpdate(update + 1);
                            rootStore.playerStore.runPlayer();
                          }}
                        />
                      )}
                    </div>
                  );
                })}
                <div
                  className="variant-add-div"
                  onClick={() => {
                    rootStore.experimentStore.createNewVariant(
                      experimentId,
                      ""
                    );
                    setUpdate(update + 1);
                    rootStore.playerStore.runPlayer();
                  }}
                >
                  <div className="experiment-option-add-div">
                    <ExperimentPlusIcon />
                  </div>
                  <span className="add-variant-text">Add Variant</span>
                  <div className="ai-icon"></div>
                  <AiIcon />
                </div>
              </div>
            </div>
          ) : (
            <>
              <Col
                span={uploaderProps ? 21 : 24}
                style={{
                  display: "flex",
                  gap: "8px",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {type === DynamicValueModTypes.date && (
                  <div id="date-picker">
                    <DatePicker
                      onChange={(x, dateString) =>
                        changeVal(
                          dayjs(x).tz(dayjs.tz.guess()).format().toString()
                        )
                      }
                      showTime
                      allowClear={false}
                      showNow={false}
                      needConfirm={false}
                      value={change.value && dayjs(change.value)}
                      variant="outlined"
                    />
                  </div>
                )}
                {showInput &&
                  (type === DynamicValueModTypes.media ? (
                    <UploadWithTextarea
                      uploadAssetsToProject={uploadAssetToProject}
                      onChange={(e) => {
                        const value = /^[1-9]\d*$/.test(e)
                          ? parseInt(e, 10)
                          : e; // If only digits without trailing 0, convert to number
                        changeVal(value);
                      }}
                      value={change.value}
                      mimeType={getMimeTypeFromAssetName(assetName)}
                      placeholder={inputPlaceholder}
                    />
                  ) : hasStructuredValue ? (
                    <Input.TextArea
                      id="connector-input"
                      value={JSON.stringify(change.value, null, 2)}
                      autoSize={{ minRows: 4, maxRows: 10 }}
                      readOnly
                    />
                  ) : (
                    <Input
                      id="connector-input"
                      value={change.value}
                      // eslint-disable-next-line no-template-curly-in-string
                      placeholder={inputPlaceholder}
                      onChange={(e) => {
                        const value = /^[1-9]\d*$/.test(e.target.value)
                          ? parseInt(e.target.value, 10)
                          : e.target.value; // If only digits without trailing 0, convert to number
                        changeVal(value);
                      }}
                      allowClear={sharedInputAllowClear}
                    />
                  ))}
                {(type === DynamicValueModTypes.number ||
                  type === DynamicValueModTypes.percentage) && (
                  <InputNumber
                    value={change.value}
                    onChange={(value) => changeVal(value)}
                    {...(type === DynamicValueModTypes.percentage
                      ? { formatter: (value, info) => (value || 0) + "%" }
                      : {})}
                    {...(type === DynamicValueModTypes.percentage
                      ? {
                          parser: (value) =>
                            Number.parseFloat(value?.replace("%", "") || "0"),
                        }
                      : {})}
                  />
                )}
                {type === DynamicValueModTypes.textarea && (
                  <Input.TextArea
                    id="connector-input"
                    placeholder={inputPlaceholder}
                    value={change.value}
                    onChange={(e) => {
                      changeVal(
                        e.target.value != null ? e.target.value : undefined
                      );
                    }}
                  />
                )}
                {type === DynamicValueModTypes.boolean && (
                  <>
                    <Switch checked={change.value} onChange={changeVal} />{" "}
                    {booleanText &&
                      (change.value ? booleanText[0] : booleanText[1])}
                  </>
                )}
                {type === DynamicValueModTypes.hex && (
                  <div id="color-picker-button">
                    <ColorPicker
                      color={change.value || ""}
                      onChange={(e) => changeVal(e.hex)}
                      size={2}
                    />
                  </div>
                )}
              </Col>
              {hasExperimentsEnabled &&
                (type === DynamicValueModTypes.textarea ||
                  type === DynamicValueModTypes.hex ||
                  showInput) &&
                !hasStructuredValue && (
                  <div className="initial-add-option">
                    <div
                      className="variant-add-div"
                      onClick={() => {
                        const vId = rootStore.experimentStore.createNewExperiment(
                          change.value,
                          ""
                        );
                        changeModExperimentId(vId);
                        setUpdate(update + 1);
                        rootStore.playerStore.runPlayer();
                      }}
                    >
                      <div className="experiment-option-add-div">
                        <ExperimentPlusIcon />
                      </div>
                      <span className="add-variant-text">Add Variant</span>
                      <div className="ai-icon"></div>
                      <AiIcon />
                    </div>
                  </div>
                )}
            </>
          )}
        </Row>
      )}

      {valueSetterType === "dataKey" && (
        sourceHelpText ? (
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <label>
              <JSONTreeSelect
                className={"mod-form-json-tree-select data-key-select"}
                json={JSON.parse(perVideoCurrentSchema || "{}") as JSONSchema7}
                value={change.dataKey}
                onChange={changeVal}
                onAddDataKey={handleAddNewDataKey}
                allowClear
              />
            </label>
            <Typography.Text type="secondary">
              {sourceHelpText}
            </Typography.Text>
          </Space>
        ) : (
          <label>
            <JSONTreeSelect
              className={"mod-form-json-tree-select data-key-select"}
              json={JSON.parse(perVideoCurrentSchema || "{}") as JSONSchema7}
              value={change.dataKey}
              onChange={changeVal}
              onAddDataKey={handleAddNewDataKey}
              allowClear
            />
          </label>
        )
      )}

      {valueSetterType === "inputName" && (
        <label>
          <Select
            showSearch
            className={"mod-form-json-tree-select"}
            style={{ width: "100%" }}
            value={change.inputName}
            placeholder="Select an input"
            onChange={changeVal}
          >
            {inputNameOption}
          </Select>
        </label>
      )}

      {valueSetterType === "liveControlKey" && ( // DEPRECATED. SHOULD ONLY BE USED FOR OLD PROJECTS
        sourceHelpText ? (
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <label>
              <JSONTreeSelect
                className={"mod-form-json-tree-select"}
                json={JSON.parse(liveControlCurrentSchema || "{}") as JSONSchema7}
                value={change.liveControlKey}
                onChange={changeVal}
                allowClear
              />
            </label>
            <Typography.Text type="secondary">
              {sourceHelpText}
            </Typography.Text>
          </Space>
        ) : (
          <label>
            <JSONTreeSelect
              className={"mod-form-json-tree-select"}
              json={JSON.parse(liveControlCurrentSchema || "{}") as JSONSchema7}
              value={change.liveControlKey}
              onChange={changeVal}
              allowClear
            />
          </label>
        )
      )}
      {valueSetterType === "expression" && (
        <JsEditor
          defaultValue={change.expression || expressionPlaceholder}
          onChange={(e: any) => changeVal(e)}
          runtimeContext="dynamicValueExpression"
          tooltipText={
            expressionTooltipText ||
            "This function will run every time the user interacts with the layers. You can use `data` and `control` objects."
          }
        />
      )}
      {renderDefaultValueEditor ? (
        valueSetterType !== "value" ? (
          renderDefaultValueEditor({
            value: change.defaultValue,
            onChange: changeDefaultVal,
          })
        ) : null
      ) : (
        !(valueSetterType === "value" && !valueContainsTemplateString) && (
          <div className="mod-extra-data-container">
            <div className="name-info-container">
              <span>Default Value</span>
            </div>
            <Input
              className="connector-default-input"
              placeholder="Displayed if no data is found"
              value={change.defaultValue}
              onChange={(e) => changeDefaultVal(e.target.value)}
              allowClear={sharedInputAllowClear}
            />
          </div>
        )
      )}
    </Space>
  );
}

export const DynamicValueMod = observer(DynamicValueModComp);
